/**
 * Os caminhos das rotas, como tupla literal.
 *
 * Precisa ser literal para o TanStack Router tipar `<Link to>` — derivar a lista de
 * `navegacao.ts` em tempo de execução apagaria os tipos e faria todo link virar
 * `string`, que é justamente o que o roteador tipado existe para evitar.
 *
 * A duplicação com `navegacao.ts` é real e tem dono declarado: a navegação é a
 * fonte do que aparece no menu, esta lista é a do que o roteador conhece, e **um
 * teste exige que toda rota da navegação exista aqui**. Sem esse teste, um item de
 * menu apontando para lugar nenhum passaria despercebido.
 */
/*
  Duas rotas existem SEM item de menu, e a razão é a mesma do `/rentabilidade`: a
  tela não está na sidebar de nenhuma das 49 telas do protótipo — chega-se a ela por
  link de outra página. `/variancia` é a 8.3 (aba irmã de Orçamento/DRE, e destino de
  três cards do catálogo 8.7) e `/tarefas/semana` é a 9.2 (atalho do Kanban, e
  destino do card "Carga semanal × capacidade").

  Elas nascem aqui e não no passo que as construir porque um card de catálogo tem de
  navegar para o destino que a spec 8.7 declara: apontá-lo para a tela vizinha seria
  a "resolução silenciosa para outro registro" que o GLOSSARIO proíbe. Até o passo
  delas chegar, as duas mostram o estado "em construção" — que é o que a pessoa vê
  em qualquer área ainda não construída, e não um 404.
*/
export const CAMINHOS = [
  '/',
  '/empresas',
  '/contatos',
  '/negocios',
  '/metas',
  '/previsao',
  '/pedidos',
  '/faturamento',
  '/recebiveis',
  '/comissoes',
  '/campanhas',
  '/fluxo-de-caixa',
  '/contas-bancarias',
  '/conciliacao',
  '/contas-a-pagar',
  '/tributos',
  '/aprovacoes',
  '/fornecedores',
  '/orcamento',
  '/variancia',
  '/plano-de-contas',
  '/ativos-intangiveis',
  '/dre',
  '/rentabilidade',
  '/relatorios',
  '/rh',
  '/tarefas',
  '/tarefas/semana',
  '/configuracoes',
  '/configuracoes/entidades',
  '/configuracoes/permissoes',
  '/configuracoes/regras-comissao',
  '/perfil',
  '/inbox',
  '/documentacao',
  '/busca',
] as const

export type Caminho = (typeof CAMINHOS)[number]

/** Rótulo de cada rota, para o estado "em construção" e para o título da página. */
export const ROTULO_POR_CAMINHO: Readonly<Record<Caminho, string>> = {
  '/': 'Visão Geral',
  '/empresas': 'Empresas',
  '/contatos': 'Contatos',
  '/negocios': 'Negócios',
  '/metas': 'Metas',
  '/previsao': 'Previsão',
  '/pedidos': 'Basquete (Pedidos)',
  '/faturamento': 'Faturamento & NFs',
  '/recebiveis': 'Recebíveis & Cobrança',
  '/comissoes': 'Comissões',
  '/campanhas': 'Campanhas',
  '/fluxo-de-caixa': 'Fluxo de Caixa',
  '/contas-bancarias': 'Contas Bancárias',
  '/conciliacao': 'Conciliação',
  '/contas-a-pagar': 'Contas a Pagar',
  '/tributos': 'Tributos',
  '/aprovacoes': 'Aprovações',
  '/fornecedores': 'Fornecedores',
  '/orcamento': 'Orçamento',
  '/variancia': 'Orçamento · Variância',
  '/plano-de-contas': 'Plano de Contas',
  '/ativos-intangiveis': 'Ativos intangíveis',
  '/dre': 'DRE & Demonstrativos',
  '/rentabilidade': 'Rentabilidade por Cliente',
  '/relatorios': 'Relatórios',
  '/rh': 'RH',
  '/tarefas': 'Tarefas',
  '/tarefas/semana': 'Tarefas · Week View',
  '/configuracoes': 'Configurações',
  '/configuracoes/entidades': 'Entidades DigitalView',
  '/configuracoes/permissoes': 'Permissões',
  '/configuracoes/regras-comissao': 'Regras de Comissão',
  '/perfil': 'Meu perfil',
  '/inbox': 'Inbox',
  '/documentacao': 'Documentação',
  '/busca': 'Busca global',
}

/**
 * Os parâmetros de busca que sobrevivem a um clique — a lista é FECHADA de propósito.
 *
 * Ela existia três vezes no arquivo (o tipo de retorno, o tipo do acumulador e o array
 * do laço), e as três precisavam ser editadas juntas. O lote F1 precisou acrescentar
 * dois deep-links e a triplicação virou defeito à espera: esquecer uma das cópias faz o
 * parâmetro atravessar a validação e ser DESCARTADO adiante, sem erro nenhum — o link
 * simplesmente não funciona, e não se sabe por quê.
 *
 * `emitir` e `pedido` são do lote F1: a ficha do pedido manda a parcela e o pedido para
 * a 4.5 abrir o registro de nota já apontando para a parcela certa.
 *
 * `empresa` é o deep-link que a spec 3.2 declara (`3.7?empresa=<uuid>`): o "Novo negócio"
 * da ficha da empresa leva a empresa junto, e o formulário abre com o Cliente já
 * escolhido. Ele é o exemplo exato do que este comentário avisa — enquanto a chave não
 * estava aqui, o roteador a DESCARTAVA no clique, sem erro nenhum.
 *
 * Mora aqui, e não em `raiz.tsx`, desde o item 09 do lote de 24/09 (D-136, DS-533): o mapa
 * único do link de entidade (`componentes/link-da-entidade-regras.ts`) usa `id`, `v`, `nf`,
 * `cat` e `scope`, e o teste dele confere cada chave contra esta lista sem importar o shell.
 * Chave do mapa que não está aqui seria descartada no clique, e o link abriria a lista.
 */
export const CHAVES_DE_BUSCA = [
  'cat', 'id', 'novo', 'editar', 'view', 'estagio', 'from', 'eixo', 'exec', 'setor',
  'conta', 'status', 'forn', 'relatorio', 'dom', 'situacao', 'aprovacao', 'v', 'scope',
  'periodo', 'de', 'ate', 'item', 'cc', 'vinculo_tipo', 'vinculo_id',
  'emitir', 'pedido', 'empresa',
  // `nf` é o deep-link da 4.4 para a 4.5 (spec 4.4 §Deep-links): a gaveta da parcela na 4.4
  // leva ao Livro de NFs já com a nota apontada.
  'nf',
  // O recorte que a 3.9 e o Dashboard guardam no endereço, para o Voltar de um registro
  // aberto na mesma aba reencontrá-lo (relato G1 · D-155 · DS-569): `visao` é a do
  // Dashboard; `praca` e `higiene` são filtros da 3.9 (com `periodo`, `de`, `ate`, `exec` e
  // `setor`, que já estavam aqui).
  'visao', 'praca', 'higiene',
  // O exercício da 8.1 (`/orcamento?ano=2027`, 26/10/2026): o seletor do cabeçalho escreve, e o
  // refresh, o Voltar e o link copiado reabrem o mesmo ano. Sem ele aqui, a troca não sobrevivia.
  'ano',
] as const
